import React, { Component } from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import PropTypes from "prop-types";
import { Header, Table } from "semantic-ui-react";
import CreditCostText from "../../../spa/component/ui/CreditCostText";
import TourJobType from "~/constants/TourJob/TourJobType";
import ProductSquareFeet from "~/constants/Product/ProductSquareFeet";
import ProductId from "~/constants/ProductId";
import PricingMethodPerSquareFeetBracketsTable from "../PricingMethodPerSquareFeetBracketsTable";

@observer
class PriceTableForPricingMethodPerSquareFeet extends Component {
  @computed
  get hasBeenDeliveredOnce() {
    const { floorPlanSize } = this.props;

    return floorPlanSize !== null;
  }

  @computed
  get hasBasicFloorPlan() {
    const { tourJobTypeId } = this.props;

    return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS;
  }

  @computed
  get hasEnhancedFloorPlan() {
    const { tourJobTypeId } = this.props;

    return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN;
  }

  /**
   * @returns {number}
   */
  @computed
  get totalCreditCost() {
    const { floorPlanSize, newEstimatedShootSize, productDiscountedCreditCosts, tourJobTypeId } = this.props;

    return this.hasBeenDeliveredOnce
      ? this.calculateCreditCostOfSquareFeetProducts(
          productDiscountedCreditCosts,
          newEstimatedShootSize > floorPlanSize ? newEstimatedShootSize : floorPlanSize,
          tourJobTypeId,
        ) - this.calculateCreditCostOfSquareFeetProducts(productDiscountedCreditCosts, floorPlanSize, tourJobTypeId)
      : this.calculateCreditCostOfSquareFeetProducts(
          productDiscountedCreditCosts,
          newEstimatedShootSize,
          tourJobTypeId,
        );
  }

  /**
   * @returns {number}
   */
  getProductIdBySquareFeetRange(floorPlanSize, tourJobTypeId = TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS) {
    if (floorPlanSize <= 2500) {
      return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
        ? ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT
        : ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT;
    }

    if (floorPlanSize <= 3500) {
      return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
        ? ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT
        : ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT;
    }

    if (floorPlanSize <= 5000) {
      return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
        ? ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT
        : ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT;
    }

    if (floorPlanSize <= 7500) {
      return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
        ? ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT
        : ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT;
    }

    if (floorPlanSize <= 10000) {
      return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
        ? ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT
        : ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT;
    }

    return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
      ? ProductId.IMMERSIVE_3D_TOUR_1000_SQ_FT
      : ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_1000_SQ_FT;
  }

  /**
   * @returns {number}
   */
  calculateNumberOfProducts(floorPlanSize) {
    if (floorPlanSize === 0) {
      return 0;
    }

    if (floorPlanSize <= 10000) {
      return 1;
    }

    return Math.round(floorPlanSize / ProductSquareFeet[ProductId.IMMERSIVE_3D_TOUR_1000_SQ_FT]);
  }

  /**
   * @returns {number}
   */
  calculateCreditCostOfSquareFeetProducts(
    productDiscountedCreditCosts,
    squareFeet,
    tourJobTypeId = TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS,
  ) {
    const floorPlanSize = squareFeet ? Math.ceil(squareFeet) : 0;

    const numberOfProducts = this.calculateNumberOfProducts(floorPlanSize);

    if (numberOfProducts === 0) {
      return 0;
    }

    const productId = this.getProductIdBySquareFeetRange(floorPlanSize, tourJobTypeId);

    return numberOfProducts * productDiscountedCreditCosts[productId];
  }

  render() {
    const {
      panoramasCount,
      newPanoramasCount,
      estimatedShootSize,
      newEstimatedShootSize,
      floorPlanSize,
      tourJobTypeId,
      productDiscountedCreditCosts,
      creditUsageLoading,
      creditUsageSummary,
    } = this.props;

    const publishTourCreditCost = productDiscountedCreditCosts[ProductId.PUBLISH_TOUR];
    let owedPublishTourCreditCost = 0;

    if (!this.hasBeenDeliveredOnce && !creditUsageLoading && creditUsageSummary.total === 0) {
      owedPublishTourCreditCost = publishTourCreditCost;
    }

    return (
      <>
        {this.hasBeenDeliveredOnce && <Header content="Summary" />}
        <Table striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{!this.hasBeenDeliveredOnce && "Summary"}</Table.HeaderCell>
              {this.hasBeenDeliveredOnce && (
                <>
                  <Table.HeaderCell>Total</Table.HeaderCell>
                  <Table.HeaderCell>Existing</Table.HeaderCell>
                </>
              )}
              <Table.HeaderCell>New</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>Panoramas</Table.Cell>
              {this.hasBeenDeliveredOnce && (
                <>
                  <Table.Cell>{panoramasCount + newPanoramasCount}</Table.Cell>
                  <Table.Cell>- {panoramasCount}</Table.Cell>
                </>
              )}
              <Table.Cell>{newPanoramasCount}</Table.Cell>
            </Table.Row>
            {newEstimatedShootSize && (
              <Table.Row>
                <Table.Cell>Single Property Website</Table.Cell>
                {this.hasBeenDeliveredOnce && (
                  <>
                    <Table.Cell>
                      <CreditCostText cost={publishTourCreditCost} />
                    </Table.Cell>
                    <Table.Cell>
                      - <CreditCostText cost={publishTourCreditCost} />
                    </Table.Cell>
                  </>
                )}
                <Table.Cell>
                  {owedPublishTourCreditCost > 0 ? <CreditCostText cost={owedPublishTourCreditCost} /> : "PAID"}
                </Table.Cell>
              </Table.Row>
            )}
            {newEstimatedShootSize && (
              <Table.Row>
                <Table.Cell>Credit Cost</Table.Cell>
                {this.hasBeenDeliveredOnce && (
                  <>
                    <Table.Cell>
                      <CreditCostText
                        cost={this.calculateCreditCostOfSquareFeetProducts(
                          productDiscountedCreditCosts,
                          newEstimatedShootSize > floorPlanSize ? newEstimatedShootSize : floorPlanSize,
                          tourJobTypeId,
                        )}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      -{" "}
                      <CreditCostText
                        cost={this.calculateCreditCostOfSquareFeetProducts(
                          productDiscountedCreditCosts,
                          floorPlanSize,
                          tourJobTypeId,
                        )}
                      />
                    </Table.Cell>
                  </>
                )}
                <Table.Cell>
                  <CreditCostText
                    cost={
                      this.hasBeenDeliveredOnce
                        ? this.calculateCreditCostOfSquareFeetProducts(
                            productDiscountedCreditCosts,
                            newEstimatedShootSize > floorPlanSize ? newEstimatedShootSize : floorPlanSize,
                            tourJobTypeId,
                          ) -
                          this.calculateCreditCostOfSquareFeetProducts(
                            productDiscountedCreditCosts,
                            floorPlanSize,
                            tourJobTypeId,
                          )
                        : this.calculateCreditCostOfSquareFeetProducts(
                            productDiscountedCreditCosts,
                            newEstimatedShootSize > estimatedShootSize ? newEstimatedShootSize : estimatedShootSize,
                            tourJobTypeId,
                          )
                    }
                  />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
          {newEstimatedShootSize && (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell>
                  <strong>Total</strong>
                </Table.HeaderCell>
                {this.hasBeenDeliveredOnce && (
                  <>
                    <Table.HeaderCell />
                    <Table.HeaderCell />
                  </>
                )}
                <Table.HeaderCell>
                  <strong>
                    <CreditCostText cost={this.totalCreditCost + owedPublishTourCreditCost} />
                  </strong>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
        <PricingMethodPerSquareFeetBracketsTable
          productDiscountedCreditCosts={productDiscountedCreditCosts}
          hasBasicFloorPlan={this.hasBasicFloorPlan}
          hasEnhancedFloorPlan={this.hasEnhancedFloorPlan}
          newEstimatedShootSize={newEstimatedShootSize}
        />
      </>
    );
  }
}

PriceTableForPricingMethodPerSquareFeet.propTypes = {
  tourJobTypeId: PropTypes.number.isRequired,
  panoramasCount: PropTypes.number.isRequired,
  newPanoramasCount: PropTypes.number.isRequired,
  estimatedShootSize: PropTypes.number,
  newEstimatedShootSize: PropTypes.number,
  floorPlanSize: PropTypes.number,
  productDiscountedCreditCosts: PropTypes.shape({
    [ProductId.PUBLISH_TOUR]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT]: PropTypes.number.isRequired,
  }).isRequired,
  creditUsageLoading: PropTypes.bool.isRequired,
  creditUsageSummary: PropTypes.object,
};

export default PriceTableForPricingMethodPerSquareFeet;
